import React, { useEffect, useState } from 'react';

import { ICONS, Icon, KIT_CSS, Modal, money, shortDate, useStyleSheet } from './kit';
import TaxFields, { TAX_CSS } from './TaxFields';

const CSS = `
.ivv-stage {
  background: #EEF0F4; border: 1px solid var(--line); border-radius: 12px;
  height: 100%; min-height: 340px; display: flex; align-items: center; justify-content: center;
  overflow: hidden; position: relative;
}
.ivv-stage iframe { width: 100%; height: 100%; border: 0; display: block; background: #fff; }
.ivv-stage img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
.ivv-stage__fallback { text-align: center; padding: 40px 28px; }
.ivv-body { display: grid; grid-template-columns: 1fr 268px; gap: 20px; height: 100%; min-height: 0; }
.ivv-side { display: flex; flex-direction: column; gap: 18px; }
.ivv-facts { display: grid; gap: 13px; }
.ivv-fact__k { font-size: 10.5px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 3px; }
.ivv-fact__v { font-size: 14px; font-weight: 700; color: var(--ink); margin: 0; word-break: break-word; }
.ivv-fact__v--big { font-size: 22px; font-weight: 800; letter-spacing: -.02em; }
@media (max-width: 860px) {
  .ivv-body { grid-template-columns: 1fr; }
  .ivv-stage { min-height: 240px; }
}
`;

/** Images render inline; anything else (a PDF) goes in an iframe. */
function isImage(url) {
  return /\.(png|jpe?g|gif|webp|heic|bmp)(\?|$)/i.test(url || '');
}

function Fact({ label, children, big }) {
  return (
    <div>
      <p className="ivv-fact__k">{label}</p>
      <p className={`ivv-fact__v${big ? ' ivv-fact__v--big iv-num' : ''}`}>{children}</p>
    </div>
  );
}

export default function InvoiceViewer({ open, invoice, onClose, canEditTax = false, onTaxSaved }) {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('ivv-viewer', CSS);
  useStyleSheet('tx-tax', TAX_CSS);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [invoice]);

  if (!invoice) return null;
  const url = invoice.invoice_url;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={invoice.campaign_name || 'Invoice'}
      subtitle={[invoice.brand_name, invoice.invoice_number].filter(Boolean).join(' · ')}
      footer={
        <>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose}>
            Close
          </button>
          {url ? (
            <a
              className="iv-btn iv-btn--primary"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon path={ICONS.download} size={15} />
              Open original
            </a>
          ) : null}
        </>
      }
    >
      <div className="ivv-body">
        <div className="ivv-stage">
          {!url || failed ? (
            <div className="ivv-stage__fallback">
              <p className="iv-empty__title">
                {url ? 'This document could not be previewed' : 'No document on file'}
              </p>
              <p className="iv-empty__text">
                {url
                  ? 'Open the original in a new tab to read it.'
                  : 'The invoice was submitted without an attachment.'}
              </p>
            </div>
          ) : isImage(url) ? (
            <img src={url} alt="Invoice document" onError={() => setFailed(true)} />
          ) : (
            <iframe title="Invoice document" src={url} onError={() => setFailed(true)} />
          )}
        </div>

        <aside className="ivv-side">
          <div className="ivv-facts">
            <Fact label="Amount" big>
              {money(invoice.amount)}
            </Fact>
            {invoice.agreed_amount ? (
              <Fact label="Agreed payout">
                <span className="iv-num">{money(invoice.agreed_amount)}</span>
              </Fact>
            ) : null}
            <Fact label="Raised on">{shortDate(invoice.submitted_at)}</Fact>
            {invoice.influencer_name ? (
              <Fact label="Creator">{invoice.influencer_name}</Fact>
            ) : null}
            <Fact label="Contact">
              {invoice.contact_phone}
              <br />
              {invoice.contact_email}
            </Fact>
            {invoice.payment_reference ? (
              <Fact label="Payment reference / UTR">{invoice.payment_reference}</Fact>
            ) : null}
            {invoice.remarks ? <Fact label="Remarks">{invoice.remarks}</Fact> : null}
          </div>

          {/* What was invoiced is above; what will actually be paid is here. */}
          <TaxFields invoice={invoice} canEdit={canEditTax} onSaved={onTaxSaved} />
        </aside>
      </div>
    </Modal>
  );
}
